/**
 * React shell. Owns "human-timescale" UI and the app state machine
 * (menu → playing → gameover, plus the POPSTAR network section). It hosts the
 * opaque <GameCanvas/> and talks to the game ONLY through the GameBus, and to the
 * backend ONLY through the GameServices facade. No per-frame state lives here.
 *
 * Tiers (docs/BARRAGE_IMPLEMENTATION_PLAN.md §2): anonymous play gets play + the
 * global leaderboard + presence; the full POPSTAR network (profile/stickers/etc.)
 * is gated behind a named login via {@link NetworkShell} + {@link AuthScreen}.
 *
 * On game-over the run flows up via the bus as a replayable {@link RunRecord};
 * this is the ONLY place a run is submitted, via `services.submitRun(record)` —
 * the backend (server, or the trusted local sim) computes the authoritative score.
 *
 * REPLAYS: the network screens (My Replays / showcase card) fetch a stored run's
 * full {@link RunRecord} and bubble it up here via the shell's `onWatchReplay`. App
 * owns the deterministic replay PLAYER — a short-lived Phaser canvas
 * ({@link ReplayHost}, booted with {@link bootReplay}) mounted on the `replay`
 * screen — and returns to the network section when the viewer taps Done. This keeps
 * the live game and the replay player on separate, never-overlapping canvases.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type Phaser from 'phaser';
import { GameBus } from './game/events';
import type { GameOverPayload } from './game/events';
import { bootReplay } from './game/bootGame';
import { SKINS, loadSkinId, saveSkinId } from './game/skins';
import type { RunRecord } from './core/types';
import { createServices } from './services';
import type { GameServices, RunReward, ScoreEntry, ServicesMode } from './services';
import type { AccountTier } from './services/types';
import { savedReplayQuota } from './services/dataModel';
import { GameCanvas } from './ui/GameCanvas';
import { Menu } from './ui/Menu';
import type { MenuProfile } from './ui/Menu';
import { Hud } from './ui/Hud';
import { GameOverModal } from './ui/GameOverModal';
import { NetworkShell } from './ui/NetworkShell';
import type { ShellRoute } from './ui/NetworkShell';
import { I18nProvider } from './ui/i18n';
import type { Lang } from './ui/i18n';

type Screen = 'menu' | 'playing' | 'gameover' | 'network' | 'replay';

export default function App() {
  // One bus + one services instance for the app's lifetime.
  const busRef = useRef<GameBus | null>(null);
  if (!busRef.current) busRef.current = new GameBus();
  const bus = busRef.current;
  const servicesRef = useRef<GameServices | null>(null);

  const [screen, setScreen] = useState<Screen>('menu');
  const [ready, setReady] = useState(false);
  const [mode, setMode] = useState<ServicesMode>('local');
  const [tier, setTier] = useState<AccountTier>('anon');
  const [onlineCount, setOnlineCount] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [leaderboard, setLeaderboard] = useState<ScoreEntry[]>([]);
  const [finalScore, setFinalScore] = useState(0);
  const [isNewBest, setIsNewBest] = useState(false);
  const [saving, setSaving] = useState(false);
  /** True when the run was recorded locally because the online submit failed. */
  const [saveError, setSaveError] = useState(false);
  /**
   * False when the server REJECTED the run (its re-simulation disagreed with the
   * client score beyond tolerance) — nothing was recorded and no XP awarded. Drives
   * an honest "couldn't verify this run" notice instead of "Score saved." With the
   * deterministic core this shouldn't happen for honest play; it's a safety net.
   */
  const [accepted, setAccepted] = useState(true);
  const [skinId, setSkinId] = useState<string>(() => loadSkinId());
  /**
   * The named player's headline stats for the start-page corner card (name /
   * level / tokens), or null for an anonymous user. Read ONCE per tier change via
   * `services.getProfile()` (NAMED-ONLY) — a discrete, human-timescale fetch, not
   * per-frame. Anonymous users have no profile doc, so we leave this null and the
   * Menu shows its "log in via POPSTAR" hint instead.
   */
  const [menuProfile, setMenuProfile] = useState<MenuProfile | null>(null);
  /**
   * Briefly true while the React menu plays its exit animation (after the player
   * grabs the teased ball) before we swap to the playing screen. A discrete,
   * human-timescale flag — NOT per-frame.
   */
  const [menuExiting, setMenuExiting] = useState(false);
  /**
   * Bumped each time we return to the menu so the SAME-instance Phaser canvas
   * (which stays mounted across menu → playing → gameover) is force-remounted —
   * destroyed and re-booted on a fresh {@link StartFlowScene} with the teased ball,
   * instead of leaving the frozen MainScene on screen. A discrete navigation key.
   */
  const [gameSession, setGameSession] = useState(0);
  /**
   * When the next canvas mount should boot STRAIGHT into a run (MainScene) rather
   * than the menu's teased-ball flow. Set true by "Play Again" so a restart is a
   * clean, reliable fresh canvas; reset false on return to the menu.
   */
  const [directRun, setDirectRun] = useState(false);
  // The run currently being watched on the `replay` screen, or null when idle. The
  // network section launches it via the shell's `onWatchReplay`; we return there
  // (the screen below the replay) when the viewer is done.
  const [replayRecord, setReplayRecord] = useState<RunRecord | null>(null);
  // Where to return after the dedicated replay canvas closes (network hub vs the
  // game-over modal, depending on where the watch was launched from).
  const [replayReturnTo, setReplayReturnTo] = useState<'network' | 'gameover'>('network');
  // The just-finished run, kept so the game-over screen can replay/save it.
  const lastRecordRef = useRef<RunRecord | null>(null);
  const [lastRunId, setLastRunId] = useState<string | null>(null);
  // SAVED-replay state for the game-over "Save replay" action (NAMED users). `saved`
  // flips true once this run has been added to the user's curated collection this
  // session; `savingReplay` guards the in-flight request; `saveQuotaFull` is true
  // when the named user is already at their tier's saved-replay quota (so the
  // button is shown-but-disabled with an honest reason rather than vanishing).
  // These are discrete, human-timescale flags — never per-frame. Starring (the
  // public showcase pin) is NOT done here anymore: it moved to My Replays, where a
  // run can only be starred once it has been SAVED.
  const [saved, setSaved] = useState(false);
  const [savingReplay, setSavingReplay] = useState(false);
  const [saveQuotaFull, setSaveQuotaFull] = useState(false);
  /**
   * The XP/level/token/achievement reward the server returned for the just-finished
   * run (NAMED users), driving the game-over XP bar + level-up ceremony. Null for
   * anonymous users / rejected runs. A discrete, human-timescale value.
   */
  const [reward, setReward] = useState<RunReward | null>(null);
  /**
   * A one-shot deep-link applied when the POPSTAR section mounts (e.g. the level-up
   * "Spin the Prize Wheel" reward opens Stickers → Prize Wheel). Cleared to null on
   * a normal open so subsequent visits land on Home.
   */
  const [networkRoute, setNetworkRoute] = useState<ShellRoute | null>(null);

  const refreshBoard = useCallback(async () => {
    const svc = servicesRef.current;
    if (!svc) return;
    const [board, hi] = await Promise.all([svc.getLeaderboard(10), svc.getHighScore()]);
    setLeaderboard(board);
    setHighScore(hi);
  }, []);

  // Load the named player's headline stats for the start-page corner card (a
  // discrete, human-timescale read — never per-frame). Anonymous accounts have no
  // profile (getProfile is NAMED-ONLY and throws RequiresAccountError), so we
  // clear it and the Menu falls back to its "log in via POPSTAR" hint.
  const refreshProfile = useCallback(async () => {
    const svc = servicesRef.current;
    if (!svc || svc.tier !== 'named') {
      setMenuProfile(null);
      return;
    }
    try {
      const p = await svc.getProfile();
      setMenuProfile({ name: p.name, level: p.level, tokens: p.tokens });
    } catch {
      // Profile unavailable (anon / offline) — show the anon hint, not an error.
      setMenuProfile(null);
    }
  }, []);

  // game-over flows up via the bus; this is the only place we submit a run.
  const handleGameOver = useCallback(
    async (payload: GameOverPayload) => {
      // Show the client's display score immediately; correct it from the server.
      setFinalScore(payload.score);
      setSaveError(false);
      setAccepted(true);
      setScreen('gameover');
      setSaving(true);
      // Stash the just-finished run so the game-over screen can replay/save it.
      lastRecordRef.current = payload.record;
      setLastRunId(null);
      // Reset the SAVED-replay action for the new run (Save, not Star — starring
      // moved to My Replays).
      setSaved(false);
      setSavingReplay(false);
      setSaveQuotaFull(false);
      setReward(null);
      const svc = servicesRef.current;
      if (svc) {
        try {
          const prevHigh = await svc.getHighScore();
          const result = await svc.submitRun(payload.record);
          // The authoritative score (server re-sim, or trusted local sim) wins.
          setFinalScore(result.score);
          setHighScore(result.best);
          setAccepted(result.accepted);
          setIsNewBest(result.accepted && result.score > prevHigh);
          setLastRunId(result.runId ?? null);
          // The XP/level/token/achievement reward (NAMED) drives the after-game bar.
          setReward(result.reward ?? null);
          await refreshBoard();
          // For a NAMED user, see whether they're already at their saved-replay
          // quota so the "Save replay" button is honestly disabled at the cap. A
          // single human-timescale read — never per-frame.
          if (svc.tier === 'named') {
            try {
              const profile = await svc.getProfile();
              setSaveQuotaFull(profile.savedReplayIds.length >= savedReplayQuota(profile.tier));
            } catch {
              // Profile unavailable (offline) — leave the button enabled; the
              // backend still enforces the quota on save.
            }
          }
        } catch {
          // Submission failed (offline / rejected); keep the displayed score and
          // tell the player it was only recorded locally.
          setSaveError(true);
        }
      }
      setSaving(false);
    },
    [refreshBoard],
  );

  // Boot services + subscribe to the bus once.
  useEffect(() => {
    let cancelled = false;
    let unsubOnline = () => {};

    void (async () => {
      const svc = await createServices();
      servicesRef.current = svc;
      await svc.init();
      if (cancelled) return;
      setMode(svc.mode);
      setTier(svc.tier);
      unsubOnline = svc.subscribeOnlineCount((n) => setOnlineCount(n));
      await refreshBoard();
      await refreshProfile();
      setReady(true);
    })();

    const unsubGameOver = bus.onGameOver((payload) => {
      void handleGameOver(payload);
    });

    // The player grabbed the teased ball (StartFlowScene began a run): elegantly
    // dismiss the menu, then reveal the game. One-shot boundary signal.
    const unsubRunStart = bus.onRunStart(() => {
      setIsNewBest(false);
      setMenuExiting(true);
      window.setTimeout(() => {
        setScreen('playing');
        setMenuExiting(false);
      }, 260);
    });

    return () => {
      cancelled = true;
      unsubOnline();
      unsubGameOver();
      unsubRunStart();
      servicesRef.current?.dispose();
    };
  }, [bus, refreshBoard, refreshProfile, handleGameOver]);

  // Play-button FALLBACK: commit the run (solo) without a drag. StartFlowScene's
  // `start` command begins the run, which echoes back via onRunStart above.
  const onPlay = useCallback(() => {
    bus.sendCommand('start');
  }, [bus]);

  const onRestart = useCallback(() => {
    setIsNewBest(false);
    // Remount the canvas straight into a fresh run — reliable every time, with no
    // dependency on a restart command reaching the (possibly transitioning) scene.
    setDirectRun(true);
    setGameSession((n) => n + 1);
    setScreen('playing');
  }, []);

  const onMenu = useCallback(() => {
    setMenuExiting(false);
    // Force a fresh StartFlowScene (teased ball) rather than the frozen MainScene.
    setDirectRun(false);
    setGameSession((n) => n + 1);
    setScreen('menu');
    void refreshBoard();
  }, [refreshBoard]);

  const onSelectSkin = useCallback((id: string) => {
    setSkinId(id);
    saveSkinId(id);
  }, []);

  const onOpenNetwork = useCallback(() => {
    setNetworkRoute(null);
    setScreen('network');
  }, []);

  // Level-up reward: open POPSTAR straight into Stickers → Prize Wheel so the player
  // can spend their spin (a one-shot deep-link consumed when the shell mounts).
  const onGoToWheel = useCallback(() => {
    setNetworkRoute({ tab: 'stickers', stickerPane: 'wheel' });
    setScreen('network');
  }, []);

  // Persist the user's UI-language choice to their account (best-effort; device-local
  // localStorage drives the UI). Wired to the i18n provider's onChange below.
  const onLangChange = useCallback((l: Lang) => {
    void servicesRef.current?.setLanguage(l);
  }, []);

  // Re-read the tier after an auth change inside the network shell. Also refresh
  // the corner-card profile so a fresh login/logout is reflected on return to menu.
  const onTierChange = useCallback(() => {
    const svc = servicesRef.current;
    if (svc) setTier(svc.tier);
    void refreshBoard();
    void refreshProfile();
  }, [refreshBoard, refreshProfile]);

  // A network screen (My Replays / showcase card) loaded a full RunRecord and
  // wants it played back: stash it and switch to the dedicated replay canvas.
  const onWatchReplay = useCallback((record: RunRecord) => {
    setReplayReturnTo('network');
    setReplayRecord(record);
    setScreen('replay');
  }, []);

  // Watch the run that JUST ended, from the game-over screen. Pure local playback
  // (re-runs the recorded inputs) so it works for anon too — no backend needed.
  const onWatchLastReplay = useCallback(() => {
    if (!lastRecordRef.current) return;
    setReplayReturnTo('gameover');
    setReplayRecord(lastRecordRef.current);
    setScreen('replay');
  }, []);

  // Save the just-finished run to the user's curated replay library (named users
  // only). Starring (the public showcase pin) is NOT done here — a run must be
  // SAVED first, then starred in My Replays.
  const onSaveLastReplay = useCallback(async () => {
    const svc = servicesRef.current;
    if (!svc || !lastRunId) return;
    setSavingReplay(true);
    try {
      await svc.saveReplay(lastRunId);
      setSaved(true);
    } catch {
      // Save failed (offline / over quota / rejected) — surface the quota cap so
      // the button stays honest; the action remains available to retry otherwise.
      setSaveQuotaFull(true);
    } finally {
      setSavingReplay(false);
    }
  }, [lastRunId]);

  // Done watching → tear the replay canvas down and return where we came from
  // (the network hub, or the game-over modal for a just-finished run).
  const onCloseReplay = useCallback(() => {
    setReplayRecord(null);
    setScreen(replayReturnTo);
  }, [replayReturnTo]);

  return (
    <I18nProvider onChange={onLangChange}>
    <div className="app">
      {/* Phaser stays mounted across menu → playing → gameover as ONE instance:
          the teased ball lives on the menu (StartFlowScene), hands off to MainScene
          on drag-start, and the frozen run shows behind the game-over modal. It
          unmounts (and is destroyed) only on return to menu — which re-mounts a
          fresh StartFlowScene with the teased ball. */}
      {(screen === 'menu' || screen === 'playing' || screen === 'gameover') && (
        <GameCanvas key={gameSession} bus={bus} skinId={skinId} directRun={directRun} />
      )}

      {screen === 'menu' &&
        (ready ? (
          <Menu
            mode={mode}
            highScore={highScore}
            leaderboard={leaderboard}
            skins={SKINS}
            skinId={skinId}
            exiting={menuExiting}
            profile={menuProfile}
            onSelectSkin={onSelectSkin}
            onPlay={onPlay}
            onOpenNetwork={onOpenNetwork}
          />
        ) : (
          <div className="screen loading">Loading…</div>
        ))}

      {screen === 'network' && servicesRef.current && (
        <NetworkShell
          services={servicesRef.current}
          tier={tier}
          onlineCount={onlineCount}
          onExit={onMenu}
          onTierChange={onTierChange}
          onWatchReplay={onWatchReplay}
          initialRoute={networkRoute}
          onRouteConsumed={() => setNetworkRoute(null)}
        />
      )}

      {/* Deterministic replay player — its own short-lived Phaser canvas, never
          mounted at the same time as the live game's <GameCanvas/>. */}
      {screen === 'replay' && replayRecord && (
        <ReplayHost record={replayRecord} skinId={skinId} onClose={onCloseReplay} />
      )}

      {screen === 'playing' && <Hud onMenu={onMenu} />}

      {screen === 'gameover' && (
        <GameOverModal
          finalScore={finalScore}
          highScore={highScore}
          isNewBest={isNewBest}
          saving={saving}
          saveError={saveError}
          accepted={accepted}
          reward={reward}
          onSpinWheel={tier === 'named' ? onGoToWheel : undefined}
          onWatchReplay={onWatchLastReplay}
          onSave={tier === 'named' ? onSaveLastReplay : undefined}
          canSave={tier === 'named' && lastRunId !== null && !saveError && !saved && !saveQuotaFull}
          saved={saved}
          savingReplay={savingReplay}
          saveQuotaFull={saveQuotaFull}
          onRestart={onRestart}
          onMenu={onMenu}
        />
      )}
    </div>
    </I18nProvider>
  );
}

/**
 * Stateless Phaser host for a single replay — the deterministic playback player.
 * Mirrors {@link GameCanvas}'s discipline: a ref'd container + a `useEffect` that
 * boots the game on mount (via {@link bootReplay}) and destroys it on unmount, so
 * it holds NO React state and never re-renders while the replay runs. A thin React
 * overlay provides the only human-timescale control we need from outside the
 * canvas — a "Done" button that returns to the network section (the in-canvas
 * play / pause / restart transport lives in {@link import('./game/ReplayScene').ReplayScene}).
 *
 * Re-mounting on a new `record` is keyed by the parent (the screen unmounts when
 * `replayRecord` clears), so each watch gets a fresh, isolated canvas — the live
 * game's {@link GameCanvas} is never mounted at the same time.
 */
function ReplayHost({
  record,
  skinId,
  onClose,
}: {
  record: RunRecord;
  skinId: string;
  onClose: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let game: Phaser.Game | null = bootReplay(el, record, skinId);

    return () => {
      // true → also remove the <canvas> from the DOM.
      game?.destroy(true);
      game = null;
    };
  }, [record, skinId]);

  return (
    <div className="replay-host">
      <div ref={containerRef} className="game-canvas" />
      <button
        className="btn btn-ghost replay-host-done"
        type="button"
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 'calc(env(safe-area-inset-top) + 12px)',
          right: 'calc(env(safe-area-inset-right) + 12px)',
          zIndex: 10,
        }}
      >
        Done
      </button>
    </div>
  );
}
